import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../../../services/AuthService';
import { hasRole as checkRole, isCreator as checkCreator, isAdmin as checkAdmin } from '../../../utils/roleUtils';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const result = await AuthService.checkAuthentication();
      if (result.authenticated) {
        setUser(result.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification de l'authentification:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedUser = AuthService.getStoredUser();
    if (storedUser) {
      setUser(storedUser);
    }

    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    AuthService.redirectToLogin();
  };

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  const refreshAuth = async () => {
    await checkAuth();
  };

  const isAuthenticated = () => !!user;

  const hasRole = (roleName) => checkRole(user, roleName);

  const isAdmin = () => checkAdmin(user);

  const isCreator = () => checkCreator(user);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        login,
        logout,
        isAdmin,
        isCreator,
        hasRole,
        refreshAuth,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    return {
      user: null,
      isAuthenticated: () => false,
      login: () => {},
      logout: () => {},
      isAdmin: () => false,
      isCreator: () => false,
      hasRole: () => false,
      refreshAuth: () => {},
      loading: true,
    };
  }

  return context;
};
